import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import classNames from 'classnames';

// @ts-expect-error
import {ActionMenu, SelectMenuOption} from '../../action-menu/action-menu';
import {
    SavedSettingToast,
    // @ts-expect-error
    SavedSettingToastState,
} from '../../saved-setting-toast/saved-setting-toast';
// @ts-expect-error
import {Level, StatusBadge} from '../../status-badge/status-badge';
// @ts-expect-error
import {Toolbar, ToolbarOption, ActionMenuOptions} from '../../toolbar/toolbar';
import {Body} from '../../typography/body';

import {RowIcon} from '../row-icon';
import type {RowType} from '../types';

import {TertiaryAction} from './tertiary-action';
import type {TertiaryActionProps, TertiaryActionPropsButton} from './tertiary-action';

import styles from './base-row.module.css';

type BackgroundColorType = 'default' | 'green';
export type BaseRowStatusBadge = {
    text: string;
    level: Level;
    icon: React.ComponentType<any>;
    detail?: string;
    onClick?: (e: React.SyntheticEvent<HTMLElement>) => void;
    getRef?: (ref?: HTMLElement | null | undefined) => void;
};

export type ToolbarProps = {
    options: ToolbarOption[];
    actionMenuOptions?: ActionMenuOptions;
    forceShowToolbar?: boolean;
    forceHideToolbar?: boolean;
};

type Props = {
    type: RowType;
    url?: string;
    onClick?: (arg1: PointerEvent) => void;
    handleMouseOver?: (e: MouseEvent) => unknown;
    handleMouseLeave?: (e: MouseEvent) => unknown;
    actionMenuOptions?: SelectMenuOption[];
    getActionMenuRef?: (arg1?: HTMLElement | null | undefined) => void;
    getRowRef?: {
        current: HTMLElement | null | undefined;
    };
    children: React.ReactNode;
    title?: string;
    titleColor?: 'dark' | 'light';
    titleIsStrikethrough?: boolean;
    overflowHidden?: boolean;
    // Optional save toast state; when provided, shows spinner/saved next to title
    saveToastState?: SavedSettingToastState;
    setSaveToastState?: (state: SavedSettingToastState) => void;
    tertiaryProps?: TertiaryActionProps;
    quaternaryProps?: TertiaryActionPropsButton;
    // Should only be used for thing like hoverviews that require external
    // API calls. Might also change for when we have other decals on the icons
    // such as primary, parent company, etc.
    customIcon?: React.ReactNode;
    statusBadges?: BaseRowStatusBadge[];
    toolbarProps?: ToolbarProps;
    backgroundColor?: BackgroundColorType;
    successFlashKey?: string;
    // If true, the row will have an onHover effect without needing an props.onClick or props.url
    isHoverable?: boolean;
    tooltipText?: string | null | undefined;
    isPrimary?: boolean;
};

export function BaseRow(props: Props) {
    const {titleColor = 'dark', backgroundColor = 'default'} = props;
    const [isSuccessFlashActive, setIsSuccessFlashActive] = React.useState<boolean>(false);
    const successFlashTimeoutRef = React.useRef<number | null>(null);

    const [shouldShowToolbar, setShouldShowToolbar] = React.useState<boolean>(false);

    const forceHideToolbar = props.toolbarProps?.forceHideToolbar;

    React.useEffect(() => {
        if (props.successFlashKey === undefined) {
            return;
        }

        setIsSuccessFlashActive(true);
        if (successFlashTimeoutRef.current) {
            clearTimeout(successFlashTimeoutRef.current);
        }
        successFlashTimeoutRef.current = window.setTimeout(() => {
            setIsSuccessFlashActive(false);
            successFlashTimeoutRef.current = null;
        }, 2000);
    }, [props.successFlashKey]);

    React.useEffect(() => {
        return () => {
            if (successFlashTimeoutRef.current) {
                clearTimeout(successFlashTimeoutRef.current);
                successFlashTimeoutRef.current = null;
            }
        };
    }, []);

    // Reset hover state when force-hiding so it doesn't persist across toggles
    React.useEffect(() => {
        if (forceHideToolbar) {
            setShouldShowToolbar(false);
        }
    }, [forceHideToolbar]);

    // Ignore mouseOver while force-hiding to prevent stale hover state
    const guardedSetShouldShowToolbar = React.useCallback(
        (value: boolean) => {
            if (value && forceHideToolbar) {
                return;
            }
            setShouldShowToolbar(value);
        },
        [forceHideToolbar]
    );

    const shouldRenderToolbar =
        props.toolbarProps &&
        (shouldShowToolbar || props.toolbarProps.forceShowToolbar) &&
        !forceHideToolbar &&
        props.toolbarProps.options.length > 0;

    const title = (
        <Body
            color={titleColor}
            isBold={true}
            wrapAnywhere={true}
            strikethrough={props.titleIsStrikethrough}
        >
            {props.title}
        </Body>
    );

    const childrenClassName = classNames(
        'flex',
        'flex-dir-col',
        'flex-grow',
        'min-width-0',
        'gap-2',
        'justify-center',
        {
            'overflow-hidden': props.overflowHidden,
        }
    );

    return (
        <BaseRowWrapper
            url={props.url}
            getRef={props.getRowRef}
            onClick={props.onClick}
            handleMouseOver={props.handleMouseOver}
            handleMouseLeave={props.handleMouseLeave}
            setShouldShowToolbar={guardedSetShouldShowToolbar}
            backgroundColor={backgroundColor}
            isSuccessFlashActive={isSuccessFlashActive}
            isHoverable={props.isHoverable}
            tooltipText={props.tooltipText}
        >
            <div className={styles.icon}>
                {props.customIcon || <RowIcon type={props.type} isPrimary={props.isPrimary} />}
            </div>
            <div className={childrenClassName}>
                {props.title &&
                    (props.saveToastState ? (
                        <SavedSettingToast
                            state={props.saveToastState}
                            onStateChange={props.setSaveToastState}
                            autoResetDelay={1500}
                            largerSpinner={true}
                        >
                            {title}
                        </SavedSettingToast>
                    ) : (
                        title
                    ))}
                {props.statusBadges &&
                    props.statusBadges.map((statusBadge) => {
                        return (
                            <div key={statusBadge.text} className='py-4'>
                                <StatusBadge
                                    level={statusBadge.level}
                                    text={statusBadge.text}
                                    customIcon={statusBadge.icon}
                                    detail={statusBadge.detail}
                                    size='small'
                                    onClick={statusBadge.onClick}
                                    getRef={statusBadge.getRef}
                                />
                            </div>
                        );
                    })}
                {props.children}
            </div>
            {(props.tertiaryProps || props.quaternaryProps) && (
                <div className={styles.actions}>
                    {props.tertiaryProps && <TertiaryAction {...props.tertiaryProps} />}
                    {props.quaternaryProps && <TertiaryAction {...props.quaternaryProps} />}
                </div>
            )}
            {props.actionMenuOptions && props.actionMenuOptions.length > 0 && (
                <ActionMenu
                    size='small'
                    hasHoverBackground={false}
                    fitContent={true}
                    options={props.actionMenuOptions}
                    getRef={props.getActionMenuRef}
                />
            )}
            {shouldRenderToolbar && props.toolbarProps && (
                <div className={styles['toolbar-container']}>
                    <Toolbar
                        options={props.toolbarProps.options}
                        actionMenuOptions={props.toolbarProps.actionMenuOptions}
                    />
                </div>
            )}
        </BaseRowWrapper>
    );
}

type BaseRowWrapperProps = {
    url?: string;
    getRef?: {
        current: HTMLElement | null | undefined;
    };
    onClick?: (arg1: PointerEvent) => void;
    handleMouseOver?: (e: MouseEvent) => unknown;
    handleMouseLeave?: (e: MouseEvent) => unknown;
    children: React.ReactNode;
    setShouldShowToolbar: (shouldShowToolbar: boolean) => void;
    backgroundColor?: BackgroundColorType;
    isSuccessFlashActive?: boolean;
    isHoverable?: boolean;
    tooltipText?: string | null | undefined;
};

function BaseRowWrapper(props: BaseRowWrapperProps) {
    const [isDragging, setIsDragging] = React.useState(false);

    const handleClick = (e: PointerEvent) => {
        if (isDragging) {
            e.preventDefault();
            setIsDragging(false);
        } else if (props.onClick) {
            props.onClick(e);
        }
    };

    const handleDragStart = (e: DragEvent) => {
        // Prevent the link from being draggable so text selection works
        e.preventDefault();
    };

    const handleMouseDown = () => {
        const selection = window.getSelection();
        // Clear the selection to allow selecting then clicking the row
        // @ts-expect-error
        selection.removeAllRanges();
        setIsDragging(false);
    };

    const handleMouseUp = () => {
        const selection = window.getSelection();
        // @ts-expect-error
        setIsDragging(selection && selection.toString().length > 0);
    };

    const linkStyleNames = classNames(styles['row--clickable'], styles.hoverable, {
        [styles['bg-green']]: props.backgroundColor === 'green',
        [styles['bg-green-hover']]: props.backgroundColor === 'green',
        [styles['bg-green-dk']]: props.isSuccessFlashActive,
        [styles['bg-green-dk-hover']]: props.isSuccessFlashActive,
    });

    if (props.url) {
        return (
            // @ts-expect-error
            <RoutedLink
                to={props.url}
                // @ts-expect-error
                onClick={handleClick}
                // @ts-expect-error
                onDragStart={handleDragStart}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseOver={(e) => {
                    // @ts-expect-error
                    props.handleMouseOver?.(e);
                    props.setShouldShowToolbar(true);
                }}
                onMouseLeave={(e) => {
                    // @ts-expect-error
                    props.handleMouseLeave?.(e);
                    props.setShouldShowToolbar(false);
                }}
                // @ts-expect-error
                ref={props.getRef}
                data-tip={props.tooltipText || undefined}
                className={linkStyleNames}
            >
                {props.children}
            </RoutedLink>
        );
    }

    const rowStyleNames = classNames({
        [styles.row]: !props.onClick,
        [styles['row--clickable']]: props.onClick,
        [styles['bg-green']]: props.backgroundColor === 'green',
        [styles['bg-green-hover']]:
            props.backgroundColor === 'green' && (props.onClick || props.isHoverable),
        [styles['bg-green-dk']]: props.isSuccessFlashActive,
        [styles['bg-green-dk-hover']]:
            props.isSuccessFlashActive && (props.onClick || props.isHoverable),
        [styles.hoverable]: props.onClick || props.isHoverable,
    });

    return (
        <div
            // @ts-expect-error
            ref={props.getRef}
            // @ts-expect-error
            onClick={handleClick}
            onMouseOver={() => props.setShouldShowToolbar(true)}
            onMouseLeave={() => props.setShouldShowToolbar(false)}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            data-tip={props.tooltipText}
            className={rowStyleNames}
        >
            {props.children}
        </div>
    );
}
